import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string, companyName: string, userName: string, referralCode?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, companyName: string, userName: string, referralCode?: string) => {
    try {
      // Call signup Edge Function (uses service role for full permissions)
      const body: Record<string, string> = { email, password, companyName, userName };
      if (referralCode) body.referralCode = referralCode;

      const response = await supabase.functions.invoke('signup', {
        body,
      });

      const { data, error } = response;

      // Handle Edge Function errors (network issues, 400/500 status codes)
      if (error) {
        console.error('Edge Function invocation error:', error);
        console.error('Edge Function error details:', JSON.stringify(error, null, 2));

        // Try to extract the actual error message from the Edge Function response
        // The error object from Supabase may contain the response body in different places
        let errorMessage = 'Unable to create account. Please try again.';

        // Check if error.context contains the parsed JSON response body (most common)
        if (error.context?.error) {
          errorMessage = error.context.error;
        }
        // Check if the error itself contains the response body
        else if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
        }
        // Check if error.message contains useful info
        else if (error.message && !error.message.includes('FunctionsHttpError') && !error.message.includes('non-2xx')) {
          errorMessage = error.message;
        }

        // Special handling for duplicate email (search entire error object)
        const errorString = JSON.stringify(error).toLowerCase();
        if (errorString.includes('already registered') || errorString.includes('email_exists') || errorString.includes('already been registered')) {
          // Try to find the exact message from backend
          const match = JSON.stringify(error).match(/"error"\s*:\s*"([^"]+already[^"]+)"/i);
          if (match) {
            errorMessage = match[1];
          } else {
            errorMessage = 'This email is already registered. Please sign in or use a different email address.';
          }
        }

        throw new Error(errorMessage);
      }

      // Check if the response indicates failure (successful HTTP but logical failure)
      if (data && data.success === false && data.error) {
        console.error('Signup validation error:', data.error);
        throw new Error(data.error);
      }

      // Check for 500 errors that return error in data
      if (data && data.error && !data.success) {
        console.error('Signup server error:', data);
        throw new Error(data.error);
      }

      // Success! New account created - now sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        console.error('Auto sign-in error:', signInError);
        throw new Error('Account created but sign-in failed. Please try logging in manually.');
      }

      // Clear any stale cached company data before proceeding
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('pulse_user_company');
        console.log('🧹 Cleared company cache for fresh signup');
      }

      return { error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Clear cached company data on login to force fresh fetch
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('pulse_user_company');
        console.log('🧹 Cleared company cache for fresh login');
      }

      return { error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    // Clear cached company data before signing out
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('pulse_user_company');
      console.log('🧹 Cleared company cache on logout');
    }

    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    isLoading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Allow exporting hook from component file for convenience
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
